import * as React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box, Paper, Table, TableBody, TableCell, tableCellClasses,
  TableContainer, TableHead, TableRow, TablePagination,
  InputBase, Divider, IconButton, Select, MenuItem,
  LinearProgress, Chip, Typography, Tooltip,
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import swal from 'sweetalert';
import { linkBackend, apiKeyProyectos } from './variables';

/* ── Estilos de celdas de tabla ── */
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#000000',
    color: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: '13px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: 'none',
    padding: '14px 18px',
    whiteSpace: 'nowrap',
  },
  [`&.${tableCellClasses.body}`]: {
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    color: '#1a2332',
    borderBottom: '1px solid #f0f2f5',
    padding: '14px 18px',
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#fdf1f3',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

/* ── Chip de estado ── */
function EstadoChip({ value }) {
  const label = value || '—';
  let color = '#626f86';
  let bg = '#f0f2f5';

  const lower = String(label).toLowerCase();
  if (lower.includes('liber') || lower === '2') {
    color = '#006644';
    bg = '#e3fcef';
  } else if (lower.includes('cerr') || lower === '4') {
    color = '#c82339';
    bg = '#fff0f2';
  } else if (lower.includes('proc') || lower === '1' || lower === '3') {
    color = '#0052cc';
    bg = '#e9f0fd';
  }

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: bg,
        color,
        fontFamily: 'Inter, sans-serif',
        fontWeight: 600,
        fontSize: '12px',
        height: '24px',
        borderRadius: '6px',
      }}
    />
  );
}

/* ── Estado vacío ── */
function EmptyState() {
  return (
    <TableRow>
      <StyledTableCell colSpan={6} sx={{ py: 8, textAlign: 'center', borderBottom: 'none' }}>
        <InboxIcon sx={{ fontSize: 48, color: '#d0d5dd', mb: 1.5, display: 'block', mx: 'auto' }} />
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: '#9aa5b4' }}>
          Ingresa un NIT o cédula para consultar los proyectos
        </Typography>
      </StyledTableCell>
    </TableRow>
  );
}

/* ══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
══════════════════════════════════════════════════════════ */
export default function GetProyectos({ Datos }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [nit, setNit] = React.useState('');
  const [vector, setVector] = React.useState([]);
  const [carga, setCarga] = React.useState(false);
  const [filtroEstado, setFiltroEstado] = React.useState('Liberado');

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  async function ConsultaClienteSap() {
    if (!nit.trim()) return;
    setCarga(true);
    setVector([]);
    setPage(0);

    try {
      const response = await fetch(`${linkBackend}/consultar-cliente`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKeyProyectos },
        body: JSON.stringify({ nit: nit.trim() }),
      });

      if (response.status === 404) {
        swal('Cliente no encontrado', 'No se encontró un cliente activo con ese NIT o cédula.', 'warning');
        setCarga(false);
        return;
      }

      const data = await response.json();
      localStorage.Cliente = data.cliente.TBP_UUID;
      Datos();
      await CargarProyectoSAP(data.cliente.CBP_UUID);
    } catch (error) {
      console.error('Error al consultar cliente:', error);
      swal('Error de conexión', 'No se pudo conectar al servidor. Intenta nuevamente.', 'error');
      setCarga(false);
    }
  }

  async function CargarProyectoSAP(clienteUUID) {
    try {
      const response = await fetch(`${linkBackend}/consultar-proyectos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKeyProyectos },
        body: JSON.stringify({ clienteUUID, estado: filtroEstado }),
      });

      if (response.status === 404) {
        swal('Sin registros', 'No se encontraron proyectos para este cliente con el estado seleccionado.', 'warning');
        setCarga(false);
        return;
      }

      const data = await response.json();
      setVector(data.proyectos);
      setCarga(false);
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
      setCarga(false);
    }
  }

  const paginatedRows = vector.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>

      {/* ── CARD DE BÚSQUEDA ── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e8ecf0',
          borderRadius: '12px',
          overflow: 'hidden',
          mb: 3,
        }}
      >
        {/* Header de la card */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #e8ecf0',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <SearchIcon sx={{ color: '#c82339', fontSize: 20 }} />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '15px',
              color: '#1a2332',
            }}
          >
            Búsqueda de proyectos
          </Typography>
        </Box>

        {/* Campos de búsqueda */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Input NIT */}
          <Box
            sx={{
              flex: 1,
              minWidth: 220,
              display: 'flex',
              alignItems: 'center',
              border: '1.5px solid #e8ecf0',
              borderRadius: '8px',
              px: 1.5,
              py: 0.5,
              backgroundColor: '#f9fafb',
              transition: 'border-color 0.2s ease',
              '&:focus-within': {
                borderColor: '#c82339',
                backgroundColor: '#ffffff',
              },
            }}
          >
            <InputBase
              value={nit}
              onChange={(e) => setNit(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') ConsultaClienteSap(); }}
              placeholder="NIT o cédula del cliente"
              sx={{
                flex: 1,
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                color: '#1a2332',
                '& input::placeholder': { color: '#9aa5b4' },
              }}
              inputProps={{ 'aria-label': 'NIT del cliente' }}
            />
            {nit && (
              <Tooltip title="Limpiar">
                <IconButton
                  size="small"
                  onClick={() => { setNit(''); setVector([]); }}
                  sx={{ color: '#9aa5b4', '&:hover': { color: '#c82339' } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Selector de estado */}
          <Select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            size="small"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              borderRadius: '8px',
              minWidth: 140,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e8ecf0' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c82339' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#c82339' },
            }}
          >
            <MenuItem value="Liberado" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
              Liberado
            </MenuItem>
            <MenuItem value="Cerrado" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
              Cerrado
            </MenuItem>
          </Select>

          <Divider orientation="vertical" flexItem sx={{ borderColor: '#e8ecf0' }} />

          {/* Botón buscar */}
          <Tooltip title="Buscar proyectos">
            <IconButton
              onClick={ConsultaClienteSap}
              disabled={carga}
              sx={{
                backgroundColor: '#c82339',
                color: '#ffffff',
                width: 42,
                height: 42,
                borderRadius: '8px',
                '&:hover': { backgroundColor: '#a01d2e' },
                '&.Mui-disabled': { backgroundColor: '#f0d0d4', color: '#ffffff' },
              }}
            >
              <PersonSearchIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* ── TABLA DE RESULTADOS ── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e8ecf0',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Header de la tabla */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #e8ecf0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '15px',
              color: '#1a2332',
            }}
          >
            Proyectos encontrados
          </Typography>
          {vector.length > 0 && (
            <Chip
              label={`${vector.length} resultado${vector.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                backgroundColor: '#fff0f2',
                color: '#c82339',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '12px',
                borderRadius: '6px',
              }}
            />
          )}
        </Box>

        {/* Barra de carga */}
        {carga && (
          <LinearProgress
            sx={{
              '& .MuiLinearProgress-bar': { backgroundColor: '#c82339' },
              backgroundColor: '#fde8eb',
            }}
          />
        )}

        <TableContainer>
          <Table aria-label="Tabla de proyectos">
            <TableHead>
              <TableRow>
                <StyledTableCell># Proyecto</StyledTableCell>
                <StyledTableCell>Descripción del proyecto</StyledTableCell>
                <StyledTableCell>Estado</StyledTableCell>
                <StyledTableCell>Sede</StyledTableCell>
                <StyledTableCell>Avance</StyledTableCell>
                <StyledTableCell>Comentario</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!carga && vector.length === 0 ? (
                <EmptyState />
              ) : (
                paginatedRows.map((row, index) => (
                  <StyledTableRow key={row.Proyecto || index}>
                    <StyledTableCell>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                          fontSize: '13px',
                          color: '#c82339',
                        }}
                      >
                        {row.Proyecto}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '14px',
                          color: '#1a2332',
                          maxWidth: 250,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={row.TextProyecto}
                      >
                        {row.TextProyecto || '—'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <EstadoChip value={row.Estado} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '14px',
                          color: '#626f86',
                        }}
                      >
                        {row.Sede || '—'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#626f86' }}>
                        {row.Avance || '—'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '13px',
                          color: '#626f86',
                          maxWidth: 220,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={row.Comentario}
                      >
                        {row.Comentario || '—'}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {vector.length > 0 && (
          <Box sx={{ borderTop: '1px solid #f0f2f5' }}>
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={vector.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                '& .MuiTablePagination-toolbar': { fontFamily: 'Inter, sans-serif' },
                '& .MuiTablePagination-selectLabel': { fontFamily: 'Inter, sans-serif', fontSize: '13px' },
                '& .MuiTablePagination-displayedRows': { fontFamily: 'Inter, sans-serif', fontSize: '13px' },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
